import Signal from 'libs/signals';
import api from 'api/api';
import AlertSignal from 'signals/Alert.signal';

type SignupForm = {
  name: string;
  zipCode: string;
  email: string;
  password: string;
  passwordConfirmation: string;
}

type Errors = {
  [K in keyof SignupForm]: string[];
}

type Touched = {
  [K in keyof SignupForm]?: true;
}

const FORM_INIT_STATE = {
  name: '',
  zipCode: '' ,
  email: '',
  password: '',
  passwordConfirmation: '',
}

const ERRORS_INIT_STATE = {
  name: [],
  zipCode: [],
  numberOfFullTimeEmployees: [],
  previousAnnualRevenue: [],
  email: [],
  password: [],
  passwordConfirmation: [],
}

export const $validation = Signal<{ errors: Errors, touched: Touched }>({
  errors: ERRORS_INIT_STATE,
  touched: {
    // zipCode: true,
    // yearBusinessOpened: true,
    // formOfBusinessOrganization: true,
    // numberOfFullTimeEmployees: true,
    // previousAnnualRevenue: true,
    // industries: true,
    // email: true,
    // password: true,
    // passwordConfirmation: true,
  },
});

export const $form = Signal<SignupForm & { page: number, isLoading?: boolean }>({
  ...FORM_INIT_STATE,
  page: 1,
});

function validateForm(data: SignupForm) {
  const errors: Errors = { ...ERRORS_INIT_STATE };

  const { 
    name,
    zipCode,
    email,
    password,
    passwordConfirmation
  } = data;

  if (name.length === 0) {
    errors.name = ['Cannot be empty'];
  }

  if (zipCode.length !== 5 || zipCode.match(/[^0-9]/)) {
    errors.zipCode = ['Please enter a valid zip code'];
  }

  if (!EMAIL_REGEX.test(email)) {
    errors.email = ['Please enter a valid email'];
  }

  errors.password = [];

  if (password.length < 8) {
    errors.password.push('Must be at least 8 characters long');
  }
  
  if (!HAS_UPPERCASE_REGEX.test(password)) {
    errors.password.push('Must contain at least 1 uppercase letter');
  }

  if (!HAS_LOWERCASE_REGEX.test(password)) {
    errors.password.push('Must contain at least 1 lowercase letter');
  }

  if (!HAS_NUMBER_REGEX.test(password)) {
    errors.password.push('Must contain at least 1 digit');
  }

  if (!password.split('').some(c => ALLOWED_SPECIAL_CHARACTERS.includes(c))) {
    errors.password.push('Must contain at least 1 special character');
  }

  if (password !== passwordConfirmation) {
    errors.passwordConfirmation = ['Must match password'];
  }

  return errors;
}

export function handleFindFundingFormOnBlur(e: { target: { name: string; value: any } }): void {
  const { name } = e.target;
  const touched = { ...$validation.value.touched, [name]: true };
  $validation.update({ touched });
}

export function handleFindFundingFormChange(e: { target: { name: string; value: any } }): void {
  const { name, value } = e.target;
  $form.update({ [name]: value });

  const errors = validateForm($form.value);
  $validation.update({ errors });
}

const ALLOWED_SPECIAL_CHARACTERS = [
  '!', '@', '#', '$', '%', '^', '&', '*', '(', ')', '-', '_', '+', '=', '{', '}', '[', ']', '~', '`'
];
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const HAS_UPPERCASE_REGEX = /[A-Z]/;
const HAS_NUMBER_REGEX = /\d/;
const HAS_LOWERCASE_REGEX = /[a-z]/;

export function handleBackPress(): void {
  const { page } = $form.value;

  if (page === 2 || page === 3){
    $form.update({ page: page - 1});
  }
}

export function handleNextPress(): any {
  const { page } = $form.value;
  const { errors, touched } = $validation.value;

  if (page === 1) {
    if (!touched.name || !touched.zipCode) {
      return;
    }

    if (errors.name.length > 0 || errors.zipCode.length > 0) {
      return;
    }

    $form.update({ page: 2 });
    return;
  }

  if (page === 2) {
    if (!touched.email || !touched.password || !touched.passwordConfirmation) {

      return;
    }

    if (errors.email.length > 0 || errors.password.length > 0 || errors.passwordConfirmation.length > 0){
      return;
    } 

    return handleSignup();
  }
}

export async function handleSignup(): Promise<any> {
  try {
    $form.update({ isLoading: true });

    const { 
      name,
      zipCode,
      email,
      password,
    } = $form.value;

    const payload = {
      business:  {
        name,
        zipCode,
      },
      user: {
        email,
        password,
      }
    }

    await api.post({
      path: '/signup',
      body: payload,
      isUnAuthenticatedReq: true,
    })

    AlertSignal.update({
      type: 'notification',
      variant: 'success',
      message: 'Thank you for signing up! Please log in.',
    });

    setTimeout(function() {
      window.location.replace('/');
    }, 3000);
  } catch (error) {
    console.error(error);
    if (error instanceof Error) {
      AlertSignal.update({
        type: 'notification',
        error,
        message: error.message,
      });
    } else {
      AlertSignal.update({
        type: 'notification',
        error,
        message: 'An unhandled exception has occurred.'
      });
    }
  } finally {
    $form.update({ isLoading: false });
  }
}
